if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.galleryEvents?.abort();
        this.galleryEvents = new AbortController();
        const options = { signal: this.galleryEvents.signal };
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        const desktopWidth = this.closest?.('.marame-figma-product') ? 1024 : 750;
        this.mql = window.matchMedia(`(min-width: ${desktopWidth}px)`);
        if (!this.elements.viewer) return;
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this), options);
        this.updateGalleryPills(undefined, this.elements.viewer.currentPage);
        this.elements.viewer.querySelector('[data-gallery-pills]')?.addEventListener('click', this.onPillClick.bind(this), options);
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false), options);
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.galleryEvents?.abort();
      }

      updateGalleryPills(mediaId, page) {
        const pills = this.elements.viewer.querySelector('[data-gallery-pills]');
        if (!pills) return;
        this.elements.viewer.querySelector('.marame-gallery-counter')?.setAttribute('aria-hidden', 'true');
        const slides = [...this.elements.viewer.querySelectorAll('[id^="Slide-"][data-media-id]')];
        if (pills.children.length !== slides.length || [...pills.children].some(pill => pill.tagName !== 'BUTTON')) {
          pills.replaceChildren(...slides.map(() => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'marame-gallery-pill';
            const mark = document.createElement('span');
            mark.setAttribute('aria-hidden', 'true');
            button.append(mark);
            return button;
          }));
        }
        pills.hidden = slides.length < 2;
        const selected = mediaId ? slides.findIndex(slide => slide.dataset.mediaId === mediaId) : -1;
        const index = selected >= 0 ? selected : Number.isInteger(page) ? Math.max(0, Math.min(page - 1, slides.length - 1)) : 0;
        [...pills.children].forEach((pill, position) => {
          const active = position === index;
          pill.dataset.active = String(active);
          pill.dataset.galleryTarget = slides[position].dataset.mediaId;
          pill.setAttribute('aria-current', String(active));
          pill.setAttribute('aria-controls', slides[position].parentElement.id);
          const label = pills.dataset.labelTemplate || '[index] / [total]';
          pill.setAttribute('aria-label', label.replace('[index]', position + 1).replace('[total]', slides.length));
        });
      }

      onPillClick(event) {
        const pill = event.target.closest('[data-gallery-target]');
        const pills = this.elements.viewer.querySelector('[data-gallery-pills]');
        if (!pill || !pills?.contains(pill)) return;
        const target = [...this.elements.viewer.querySelectorAll('[id^="Slide-"][data-media-id]')]
          .find(slide => slide.dataset.mediaId === pill.dataset.galleryTarget);
        if (!target) return;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.parentElement.scrollTo({ left: target.offsetLeft, behavior: reduced ? 'instant' : 'smooth' });
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset.mediaId;
        this.updateGalleryPills(mediaId, event.detail.currentPage);
        if (!this.elements.thumbnails || !mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        // A colour can have a gallery without an assigned variant hero image.
        // Use the actual fallback slide's ID for thumbnail and live-region updates.
        mediaId = activeMedia.dataset.mediaId;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.updateGalleryPills(mediaId);
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollTo({ top: top, behavior: reduceMotion ? 'instant' : 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
